import { T } from '@/i18n';
import css from './QuotaRow.module.css';

export type QuotaPart = { kind: 'used' | 'ok' | 'this'; n: number; text: string };

/**
 * One limit, the same on every absence (owner 2026-10-25, absence-decision.prototype.html): its name, the
 * figure as "x av y", one bar and its parts in numbers under it. Amber at the limit, red over. A limit that
 * counts down (vacation left) shows what is left; one that counts up (egenmeldinger) shows what is used.
 */
export function QuotaRow({
  label,
  parts,
  limit,
  down = false,
  note,
  testid,
}: {
  label: string;
  parts: QuotaPart[];
  limit: number;
  down?: boolean;
  note?: string | undefined;
  testid?: string;
}) {
  const used = parts.reduce((n, p) => n + p.n, 0);
  const shown = down ? limit - used : used;
  const state = used > limit ? css.over : used === limit ? css.full : '';
  let left = limit;
  const segs = parts.flatMap((p) => {
    const inside = Math.max(0, Math.min(p.n, left));
    left -= inside;
    return inside ? [<i key={p.kind} className={css[p.kind]} style={{ flexGrow: inside }} />] : [];
  });
  return (
    <div className={`${css.row} ${state}`} data-testid={testid}>
      <div className={css.hd}>
        <span>{label}</span>
        <b>
          {String(shown).replace('-', '−')} <small>{T('av {n}', { n: limit })}</small>
        </b>
      </div>
      <div className={css.bar} aria-hidden="true">
        {segs}
        {used > limit ? (
          <i className={css.past} style={{ flexGrow: used - limit }} />
        ) : left > 0 ? (
          <i style={{ flexGrow: left }} />
        ) : null}
      </div>
      <div className={css.parts}>
        {parts
          .filter((p) => p.n)
          .map((p) => (
            <span key={p.kind} className={css[p.kind]}>
              {p.n} {p.text}
            </span>
          ))}
        {note && <span className={css.plain}>{note}</span>}
      </div>
    </div>
  );
}
